import type { AIEvent } from '@doger/core/shared/api';

export enum UserRole {
  GUEST = 'GUEST',
  USER = 'USER',
  ADMIN = 'ADMIN',
}

export type FindManyUsersResponse = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  isActive: boolean;
  isWhitelisted: boolean;
  createdAt: string;
  updatedAt: string;
  industry: string | null;
  contact: string | null;
}[];

export type FindOneUserResponse = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  isActive: boolean;
  isWhitelisted: boolean;
  createdAt: string;
  updatedAt: string;
  industry: string | null;
  contact: string | null;
};

export type FindManyUserChatsResponse =
  | {
      id: string;
      appName: string;
      userId: string;
      state: {
        chatId: string;
        title: string;
      } & Record<string, string>;
      events: [];
      lastUpdateTime: number;
      meta: {
        id: string;
        title: string;
        userId: string;
        app: string;
        sessionId: string;
        meta: Record<string, string>;
        status: 'ACTIVE' | 'DELETED';
        createdAt: string;
        updatedAt: string;
      };
    }[]
  | {
      chatsWithSessions: {
        id: string;
        appName: string;
        userId: string;
        state: {
          chatId: string;
          title: string;
        } & Record<string, string>;
        events: [];
        lastUpdateTime: number;
        meta: {
          id: string;
          title: string;
          userId: string;
          app: string;
          sessionId: string;
          meta: Record<string, string>;
          status: 'ACTIVE' | 'DELETED';
          createdAt: string;
          updatedAt: string;
        };
      }[];
      chats: {
        id: string;
        title: string;
        userId: string;
        app: string;
        sessionId: string;
        meta: Record<string, string>;
        status: 'ACTIVE' | 'DELETED';
        createdAt: string;
        updatedAt: string;
      }[];
      sessions: {
        id: string;
        appName: string;
        userId: string;
        state: {
          chatId: string;
          title: string;
        } & Record<string, string>;
        events: [];
        lastUpdateTime: number;
      }[];
    };

export type FindOneUserChatResponse = {
  id: string;
  appName: string;
  userId: string;
  state: {
    chatId: string;
    title: string;
  } & Record<string, string>;
  events: AIEvent[];
  lastUpdateTime: number;
  meta: {
    id: string;
    title: string;
    userId: string;
    app: string;
    sessionId: string;
    meta: Record<string, string>;
    status: 'ACTIVE' | 'DELETED';
    createdAt: string;
    updatedAt: string;
  };
};

type User = FindManyUsersResponse[number];

export type Note = {
  id: string;
  userId: string;
  content: string;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;

  User: Pick<User, 'id' | 'email' | 'firstName' | 'lastName'>;
  Creator: Pick<User, 'id' | 'email' | 'firstName' | 'lastName'> | null;
};

export type FindManyUserNotes = Note[];

export type SystemMetricsResponse = {
  /** CPU usage percentage */
  cpuUsage: number;
  /** Memory usage in MB */
  memoryUsage: number;
  /** Total memory in MB */
  totalMemory: number;
  /** Memory usage percentage */
  memoryUsagePercentage: number;
  /** ISO String of the uptime */
  uptime: string;
  /** Timestamp of the metrics */
  timestamp: string;
};

export type ResponseTimeMetricsResponse = {
  /** Trimmed mean response time in milliseconds (5% trimmed from each end) */
  mean: number;
  /** Minimum response time in milliseconds */
  min: number;
  /** Maximum response time in milliseconds */
  max: number;
  /** 95th percentile response time in milliseconds */
  p95: number;
  /** 99th percentile response time in milliseconds */
  p99: number;
  /** Total number of requests */
  totalRequests: number;
};

export type ErrorRateMetricsResponse = {
  /** Total number of requests */
  totalRequests: number;
  /** Number of successful requests */
  successfulRequests: number;
  /** Number of failed requests */
  failedRequests: number;
  /** Error rate percentage */
  errorRate: number;
  /** Success rate percentage */
  successRate: number;
  /** Timestamp of the metrics */
  timestamp: string;
};

export type RequestsPerSecondResponse = {
  /** Current requests per second */
  currentRps: number;
  /** Average requests per second over the last minute */
  averageRps: number;
  /** Peak requests per second in the last hour */
  peakRps: number;
  /** Total requests in the last minute */
  totalRequestsLastMinute: number;
  /** Timestamp of the metrics */
  timestamp: string;
};

export type HealthStatus = 'healthy' | 'warning' | 'critical';

export type MetricsResponse = {
  /** System metrics */
  systemMetrics: SystemMetricsResponse;
  /** Response time metrics */
  responseTimeMetrics: ResponseTimeMetricsResponse;
  /** Error rate metrics */
  errorRateMetrics: ErrorRateMetricsResponse;
  /** Requests per second metrics */
  requestsPerSecond: RequestsPerSecondResponse;
  /** Overall system health status */
  healthStatus: HealthStatus;
  /** Timestamp of the dashboard data */
  timestamp: string;
};

export type DeviceInfo = {
  device: string;
  os: string;
  cpu: string;
};

export type SessionDto = {
  key: string;
  jwtId: string;
  userId: string;
  device: string;
  userAgent: string;
  ip: string;
  location: string;
  fingerprint: string;
  deviceInfo: DeviceInfo;
  payload: any;
  endAt: string | null;
  createdAt: string;
};

export type SessionsResponseDto = {
  sessions: SessionDto[];
};
